import type { MatrixPos, Task } from "@app/core";
import { addDays, effectivePriorityOf, endOfDay, matrixCellCenter, matrixEnabledFor, startOfDay } from "@app/core";
import { Button, Card, Grid3x3, Plus } from "@app/ui";
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { isOverdue, OVERDUE_LOOKBACK_DAYS } from "../features/agenda/agenda.js";
import type { RuleExpansionCache } from "../features/calendar-view/build-days.js";
import { expandTasksWindowed } from "../features/calendar-view/build-days.js";
import { useNowMinute } from "../features/calendar-view/useNowMinute.js";
import { buildDayLoads, buildMatrixView, createMatrixItemCache, dayKeyOf, hiddenMatrixTasks, type ViewItem } from "../features/matrix-view/model.js";
import { HiddenPanel } from "../features/matrix-view/HiddenPanel.js";
import { LoadStrip } from "../features/matrix-view/LoadStrip.js";
import { MatrixBoard } from "../features/matrix-view/MatrixBoard.js";
import { planCandidates, planDay, type PlanSlot } from "../features/matrix-view/plan-day.js";
import { PlanDayDialog } from "../features/matrix-view/PlanDayDialog.js";
import { QueuePanel } from "../features/matrix-view/QueuePanel.js";
import { TimeLadder, type LadderChoice } from "../features/matrix-view/TimeLadder.js";
import { ladderDurationMinutes, sameLadderSchedule } from "../features/matrix-view/ladder-schedule.js";
import type { DropBand } from "../features/matrix-view/useBoardDrag.js";
import { ViewControls } from "../features/matrix-view/ViewControls.js";
import { useEffectiveMatrixDisplay, useEffectivePriorityCtx } from "../features/priority-matrix/useEffectiveMatrixDisplay.js";
import { SnoozeMenu } from "../features/snooze/index.js";
import { toLocalDate } from "../features/task-form/time-helpers.js";
import { matrixAccentFromPos, PRIORITY_FLAG_HEX } from "../features/task-colors.js";
import { visibleTasks } from "../lib/calendar-visibility.js";
import { useI18n } from "../lib/useI18n.js";
import { useViewFilters } from "../lib/useViewFilters.js";
import { applyViewExclusions } from "../lib/view-exclusions.js";
import { occurrenceRefOf } from "../lib/virtual-id.js";
import { useCalendarFocusStore, useCalendarsStore, useMatrixViewStore, useSettingsStore, useTaskDialogStore, useTasksStore } from "../stores/index.js";
import { toast } from "../stores/toast.store.js";
import { beginUndoable, endUndoable, undoAction } from "../stores/undo.js";
import { type NextInstanceCache, tasksForList } from "./tasks/next-instances.js";

const STRIP_DAYS = 14;

/**
 * Priorities — the matrix decision view: every task in the chosen time range on
 * one importance×urgency board, a suggested execution order beside it, and a
 * day-by-day load strip for the next two weeks. Board and queue collapse each
 * recurring series to its next occurrence; the strip expands real occurrences.
 */
export function PrioritiesRoute() {
  const { t } = useI18n();
  const tasks = useTasksStore((s) => s.tasks);
  const rules = useTasksStore((s) => s.rules);
  const overrides = useTasksStore((s) => s.overrides);
  const loaded = useTasksStore((s) => s.loaded);
  const hydrate = useTasksStore((s) => s.hydrate);
  const edit = useTasksStore((s) => s.edit);
  const complete = useTasksStore((s) => s.complete);
  const completeOccurrence = useTasksStore((s) => s.completeOccurrence);
  const snooze = useTasksStore((s) => s.snooze);
  const snoozeOccurrence = useTasksStore((s) => s.snoozeOccurrence);
  const lastCalendarView = useSettingsStore((s) => s.lastCalendarView);
  const morningHour = useSettingsStore((s) => s.snoozeMorningHour);
  const eveningHour = useSettingsStore((s) => s.snoozeEveningHour);
  const defaultDurationMin = useSettingsStore((s) => s.defaultDurationMin);
  const focusCalendar = useCalendarFocusStore((s) => s.focus);
  const navigate = useNavigate();
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  const matrixScope = useSettingsStore((s) => s.matrixScope);
  const excludedTopicIds = useSettingsStore((s) => s.prioritiesExcludedTopicIds);
  const calendars = useCalendarsStore((s) => s.calendars);
  const applyFilters = useViewFilters();
  const displayOf = useEffectiveMatrixDisplay();
  const priorityCtx = useEffectivePriorityCtx();
  const now = useNowMinute();

  const horizon = useMatrixViewStore((s) => s.horizon);
  const includeOverdue = useMatrixViewStore((s) => s.includeOverdue);
  const includeUndated = useMatrixViewStore((s) => s.includeUndated);
  const includeDone = useMatrixViewStore((s) => s.includeDone);
  const includeEvents = useMatrixViewStore((s) => s.includeEvents);
  const dayFocus = useMatrixViewStore((s) => s.dayFocus);
  const setDayFocus = useMatrixViewStore((s) => s.setDayFocus);
  const capacityHours = useMatrixViewStore((s) => s.capacityHours);
  const setCapacityHours = useMatrixViewStore((s) => s.setCapacityHours);
  const [snoozeTarget, setSnoozeTarget] = useState<{ task: Task; x: number; y: number } | null>(null);
  /** Strip day a board chip is hovering during a drag (drop = schedule there). */
  const [dropDay, setDropDay] = useState<string | null>(null);
  /** Pending strip-day drop: the time ladder is open at the release point. */
  const [ladder, setLadder] = useState<{ item: ViewItem; dayKey: string; x: number; y: number } | null>(null);
  /** Strip day that just received a drop — flash-highlighted for a moment. */
  const [flashDay, setFlashDay] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  /** Queue band a board chip is hovering during a drag (drop = file there). */
  const [dropBand, setDropBand] = useState<DropBand | null>(null);
  /** True while a board chip drag is in flight (queue materializes empty bands). */
  const [boardDragActive, setBoardDragActive] = useState(false);
  /** "Plan my day" proposal dialog for the focused strip day. */
  const [planOpen, setPlanOpen] = useState(false);
  const planApplyingRef = useRef(false);

  useEffect(() => { if (!loaded) void hydrate(); }, [loaded, hydrate]);
  useEffect(() => () => { if (flashTimer.current) window.clearTimeout(flashTimer.current); }, []);

  const scopeOn = matrixScope !== "off";
  const withEvents = matrixScope === "tasks_events" && includeEvents;

  // Keep calendar visibility + route exclusions separate from interactive
  // decision filters. The board/queue use both; planner availability must not
  // forget a real commitment merely because a label/priority filter hides it.
  const availabilityTasks = useMemo(
    () => applyViewExclusions(visibleTasks(tasks, calendars), { topicIds: excludedTopicIds, calendarIds: [] }),
    [tasks, calendars, excludedTopicIds],
  );
  const filtered = useMemo(
    () => applyFilters(availabilityTasks),
    [availabilityTasks, applyFilters],
  );

  // Board + queue: one entry per series (its next occurrence), like All tasks.
  const nextCache = useRef<NextInstanceCache>(new Map());
  const collapsed = useMemo(
    () => tasksForList(filtered, rules, overrides, now, nextCache.current, withEvents),
    [filtered, rules, overrides, now, withEvents],
  );

  // Missed recurring occurrences (agenda lookback semantics): the collapsed
  // list only carries each series' NEXT occurrence, so with the Overdue toggle
  // on, recent misses are expanded separately and merged in. They are virtual
  // occurrences — complete/snooze/place all resolve to the source series.
  const overdueCache = useRef<RuleExpansionCache>(new Map());
  const missedOccurrences = useMemo(() => {
    if (!includeOverdue) return [];
    const recurring = filtered.filter((task) => task.recurrence_id && !task.is_event);
    if (recurring.length === 0) return [];
    const from = startOfDay(addDays(now, -OVERDUE_LOOKBACK_DAYS));
    const expanded = expandTasksWindowed(recurring, rules, overrides, from, now, overdueCache.current);
    return expanded.filter((task) => task.virtual && isOverdue(task, now));
  }, [filtered, rules, overrides, now, includeOverdue]);

  const focusDate = useMemo(() => (dayFocus ? new Date(`${dayFocus}T00:00:00`) : null), [dayFocus]);
  // Stable across rebuilds so an edit to ONE task leaves every other ViewItem
  // object identical — that is what lets the board chips / queue rows memoize.
  const coveredFn = useMemo(
    () => (task: Task) => matrixEnabledFor(matrixScope, task.is_event),
    [matrixScope],
  );
  const itemCache = useRef(createMatrixItemCache());
  const model = useMemo(
    () => buildMatrixView(
      [...collapsed, ...missedOccurrences],
      { horizon, includeOverdue, includeUndated, includeDone, dayFocus: focusDate, hidingActive: scopeOn },
      now,
      displayOf,
      coveredFn,
      itemCache.current,
    ),
    [collapsed, missedOccurrences, horizon, includeOverdue, includeUndated, includeDone, focusDate, now, displayOf, coveredFn, scopeOn],
  );

  // Load strip: recurrence-EXPANDED occurrences so a daily series weighs every day.
  // Built from the DEFERRED task list so the two-week expansion is not part of a
  // drop's own commit — the strip is a secondary view and may lag one render.
  //
  // Events ALWAYS count here, whatever the matrix scope / events toggle say. Those
  // control which entries are DECISIONS (board, tray, queue); the strip measures
  // how much of the day is already spoken for, and the planner blocks event time
  // unconditionally (see `plan` below). Filtering them out let a day of meetings
  // read as empty against the capacity bar while "Plan this day" then found no
  // free gaps in exactly those hours. Same reasoning as `matrix_hidden`: hiding
  // something from the decisions must never free its hours.
  const stripSource = useDeferredValue(filtered);
  const expansionCache = useRef<RuleExpansionCache>(new Map());
  const loads = useMemo(() => {
    const expanded = expandTasksWindowed(
      stripSource, rules, overrides, startOfDay(now), endOfDay(addDays(now, STRIP_DAYS - 1)), expansionCache.current,
      true,
    );
    return buildDayLoads(expanded, now, STRIP_DAYS);
  }, [stripSource, rules, overrides, now]);
  useEffect(() => {
    if (!dayFocus || loads.some((load) => dayKeyOf(load.date) === dayFocus)) return;
    // The strip rolls forward at local midnight. Do not leave yesterday's
    // now-invisible selection narrowing the board with no highlighted day.
    setPlanOpen(false);
    setDayFocus(null);
  }, [dayFocus, loads, setDayFocus]);

  // A board drop must pay for the chip landing and nothing else. The queue's
  // re-ranking and the strip's load bars are SECONDARY views of the same edit —
  // both re-render whole lists (a few hundred rows/dots between them), and doing
  // that inside the release's own commit is what kept a drop off the next frame.
  // Deferring their inputs hands those two renders to React at transition
  // priority: the release commits the board immediately, then the panels catch
  // up. The board's own `items`/`tray` are deliberately NOT deferred — the
  // dragged chip has to arrive on the frame the pointer comes up.
  const queueView = useDeferredValue(model.queue);

  // Same placed-gate as `useMatrixPriorityColor`: continuous diagonal color only
  // for a covered AND placed task; otherwise the discrete live-priority hue.
  const colorOf = useCallback((task: Task): string => {
    const continuous = matrixEnabledFor(matrixScope, task.is_event) && task.matrix_pos
      ? matrixAccentFromPos(displayOf(task))
      : null;
    return continuous ?? (PRIORITY_FLAG_HEX[effectivePriorityOf(task, priorityCtx)] || "#94a3b8");
  }, [matrixScope, displayOf, priorityCtx]);

  // The handlers handed to the board chips / queue rows are `useCallback`-stable
  // so those memoized components can skip re-rendering when another task's edit
  // rebuilds the model.
  const toggleComplete = useCallback(async (task: Task, done: boolean) => {
    if (task.is_event) return;
    try {
      if (task.virtual && task.parent_id) await completeOccurrence(task.parent_id, occurrenceRefOf(task), done);
      else await complete(task.id, done);
    } catch {
      toast.error(t("priorities.update.failed"));
    }
  }, [completeOccurrence, complete, t]);

  // Same virtual-occurrence resolution as the Today view's snooze.
  async function applySnooze(task: Task, atIso: string) {
    if (task.is_event) return;
    try {
      if (task.virtual && task.parent_id) await snoozeOccurrence(task.parent_id, occurrenceRefOf(task), atIso);
      else await snooze(task.id, atIso);
    } catch {
      toast.error(t("priorities.update.failed"));
    }
  }

  const viewOnCalendar = useCallback((task: Task) => {
    if (!task.due_at) return;
    focusCalendar(new Date(task.due_at), task.id);
    navigate(`/calendar/${lastCalendarView}`);
  }, [focusCalendar, navigate, lastCalendarView]);

  const openSnoozeMenu = useCallback(
    (task: Task, pos: { x: number; y: number }) => setSnoozeTarget({ task, ...pos }),
    [],
  );
  const openPlan = useCallback(() => setPlanOpen(true), []);

  // A chip dropped on a load-strip day opens the time ladder at the release
  // point; the reschedule only commits once a rung is picked (dismiss = the
  // drop is cancelled). Recurring/virtual chips keep their rhythm — the
  // calendar owns occurrence moves (this/following/all confirm).
  const scheduleOnDay = useCallback((item: ViewItem, dayKey: string, at: { x: number; y: number }) => {
    if (item.task.recurrence_id || item.task.virtual) {
      toast.info(t("priorities.strip.recurringDrop"));
      return;
    }
    setLadder({ item, dayKey, ...at });
  }, [t]);

  // Commit the ladder pick: a time rung schedules a timed slot on the target
  // day (keep-time is emitted as a plain time choice), "All day" keeps/makes
  // it all-day at local midnight. The scheduled day flashes as confirmation.
  async function commitLadder(choice: LadderChoice) {
    if (!ladder) return;
    const { item, dayKey } = ladder;
    const due = new Date(`${dayKey}T00:00:00`);
    const allDay = choice.kind === "allDay";
    if (!allDay) due.setHours(choice.hour, choice.minute, 0, 0);
    // A chip can be dropped back onto its own strip day. Presets/exact time can
    // also reproduce the current slot, so avoid updated_at churn and an Undo
    // snapshot when the resulting schedule is identical.
    if (sameLadderSchedule(item.task, due, allDay)) return;
    try {
      await edit(item.task.id, {
        due_at: due.toISOString(),
        all_day: allDay,
        duration_minutes: ladderDurationMinutes(item.task, allDay, defaultDurationMin),
      });
      flashStripDay(dayKey);
    } catch {
      toast.error(t("priorities.ladder.failed"));
    }
  }

  /** Confirmation flash on a strip day that just received scheduled work. */
  function flashStripDay(dayKey: string) {
    setFlashDay(dayKey);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashDay(null), 2300);
  }

  // A board drop is an exact MANUAL placement; the persisted `priority` snapshot
  // is refreshed to the new diagonal so non-render consumers stay in step
  // (same contract as the calendar context menu). matrix_pos is a whole-series
  // scalar — resolve virtual occurrences to their source row first.
  const placeItem = useCallback(async (item: ViewItem, pos: MatrixPos) => {
    const task = item.task;
    const sourceId = task.virtual && task.parent_id ? task.parent_id : task.id;
    const source = useTasksStore.getState().tasks.find((x) => x.id === sourceId);
    if (!source) return;
    const next: MatrixPos = { urgency: pos.urgency, importance: pos.importance };
    const priority = effectivePriorityOf({ ...source, matrix_pos: next }, priorityCtx);
    try {
      await edit(sourceId, { matrix_pos: next, priority });
    } catch {
      toast.error(t("priorities.update.failed"));
    }
  }, [edit, priorityCtx, t]);

  // Dropping a placed chip on the tray zone takes it OFF the matrix entirely:
  // in scope the matrix is the sole source of priority, so the mirrored flag
  // snapshot goes with the position — leaving it would freeze a matrix-derived
  // priority the read-only flag rail gives the user no way to change. Same
  // write as the form's Clear. (The placed-gate fallback still serves tasks
  // that were NEVER placed — their flag is the user's own, not a snapshot.)
  const unplaceItem = useCallback(async (item: ViewItem) => {
    const task = item.task;
    const sourceId = task.virtual && task.parent_id ? task.parent_id : task.id;
    try {
      await edit(sourceId, { matrix_pos: null, priority: "none" });
    } catch {
      toast.error(t("priorities.update.failed"));
    }
  }, [edit, t]);

  // A chip dropped on a queue band header/section: coarse placement at that
  // band's diagonal center — same write contract as an exact board drop.
  const placeInBand = useCallback((item: ViewItem, band: DropBand) => {
    const idx = band === "later" ? 0 : band === "next" ? 1 : 2;
    const center = matrixCellCenter(idx);
    void placeItem(item, { urgency: center, importance: center });
  }, [placeItem]);

  const handlePlace = useCallback((item: ViewItem, pos: MatrixPos) => { void placeItem(item, pos); }, [placeItem]);
  const handleUnplace = useCallback((item: ViewItem) => { void unplaceItem(item); }, [unplaceItem]);

  // Hiding is a WHOLE-SERIES decision (the point of it: "sleep"/"work" repeat
  // and are never actionable), so — like every other matrix write here — a
  // virtual occurrence resolves to its source row first. The item keeps
  // occupying time: the load strip and the planner read the task list directly,
  // not the decision model.
  const setHidden = useCallback(async (task: Task, hidden: boolean) => {
    const sourceId = task.virtual && task.parent_id ? task.parent_id : task.id;
    try {
      await edit(sourceId, { matrix_hidden: hidden });
    } catch {
      toast.error(t("priorities.update.failed"));
    }
  }, [edit, t]);

  const handleHide = useCallback((item: ViewItem) => {
    const task = item.task;
    void setHidden(task, true).then(() => {
      toast.withAction("info", t("priorities.hide.toast"), {
        label: t("priorities.hidden.show"),
        onClick: () => void setHidden(task, false),
      });
    });
  }, [setHidden, t]);

  const handleShow = useCallback((task: Task) => { void setHidden(task, false); }, [setHidden]);

  // Not horizon-filtered on purpose — see `hiddenMatrixTasks`. Empty while the
  // matrix is off: hiding does not apply then (the model keeps those entries in
  // the queue), so there is nothing to recover and no board for the panel to
  // talk about.
  const hiddenTasks = useMemo(() => (scopeOn ? hiddenMatrixTasks(collapsed) : []), [collapsed, scopeOn]);

  // "Plan my day": propose times for the focused day's plannable queue items in
  // the gaps between existing timed blocks. Candidates come from the FULL-range
  // decision order (not the day-narrowed model) so undated/overdue backlog is
  // pull-in-able; busy blocks come from the recurrence-expanded day including
  // events — a meeting blocks time even when the matrix scope excludes events.
  const planBusyCache = useRef<RuleExpansionCache>(new Map());
  const plan = useMemo(() => {
    if (!planOpen || !focusDate) return null;
    const full = buildMatrixView(
      [...collapsed, ...missedOccurrences],
      { horizon, includeOverdue, includeUndated, includeDone: false, dayFocus: null, hidingActive: scopeOn },
      now,
      displayOf,
      (task) => matrixEnabledFor(matrixScope, task.is_event),
    );
    const candidates = planCandidates(full.queue.map((i) => i.task), focusDate, now);
    const candidateIds = new Set(candidates.map((t) => t.id));
    const busy = expandTasksWindowed(
      availabilityTasks, rules, overrides, startOfDay(focusDate), endOfDay(focusDate), planBusyCache.current,
      true,
    ).filter((t) => !candidateIds.has(t.id));
    return planDay(
      candidates, busy, focusDate,
      { startHour: morningHour, endHour: eveningHour, fallbackMinutes: defaultDurationMin },
      now,
    );
  }, [
    planOpen, focusDate, collapsed, missedOccurrences, horizon, includeOverdue, includeUndated,
    now, displayOf, matrixScope, scopeOn, availabilityTasks, rules, overrides, morningHour, eveningHour, defaultDurationMin,
  ]);

  async function applyPlan(slots: PlanSlot[]) {
    if (planApplyingRef.current) return;
    planApplyingRef.current = true;
    setPlanOpen(false);
    let enteredUndo = false;
    let outer = false;
    try {
      enteredUndo = true;
      outer = await beginUndoable("Plan day");
      for (const s of slots) {
        await edit(s.task.id, {
          due_at: s.start.toISOString(),
          all_day: false,
          duration_minutes: s.minutes,
        });
      }
      if (dayFocus) flashStripDay(dayFocus);
      if (outer) toast.withAction("success", t("priorities.plan.applied", { count: slots.length }), undoAction());
    } catch {
      setPlanOpen(true);
      if (outer) toast.withAction("error", t("priorities.plan.failed"), undoAction());
      else toast.error(t("priorities.plan.failed"));
    } finally {
      if (enteredUndo) endUndoable();
      planApplyingRef.current = false;
    }
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="min-w-0 break-words text-2xl font-semibold">{t("priorities.title")}</h1>
        <Button onClick={() => openCreate(undefined, undefined, false)}>
          <Plus className="h-4 w-4" /> {t("common.newTask")}
        </Button>
      </header>

      <ViewControls openCount={model.openCount} totalMinutes={model.totalMinutes} />

      <LoadStrip
        loads={loads}
        selected={dayFocus}
        onSelect={setDayFocus}
        colorOf={colorOf}
        dropDay={dropDay}
        flashDay={flashDay}
        capacityHours={capacityHours}
        onCapacityChange={setCapacityHours}
        onPlan={openPlan}
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        {scopeOn ? (
          <Card className="overflow-visible p-0">
            <MatrixBoard
              items={model.board}
              tray={model.tray}
              now={now}
              onPlace={handlePlace}
              onUnplace={handleUnplace}
              onOpen={openEdit}
              onHide={handleHide}
              onDropOnDay={scheduleOnDay}
              onHoverDay={setDropDay}
              onDropOnBand={placeInBand}
              onHoverBand={setDropBand}
              onDragActive={setBoardDragActive}
            />
          </Card>
        ) : (
          <EnableMatrixCard />
        )}
        <QueuePanel
          queue={queueView}
          now={now}
          onToggle={toggleComplete}
          onOpen={openEdit}
          onSnooze={openSnoozeMenu}
          onView={viewOnCalendar}
          dropBand={dropBand}
          dragActive={boardDragActive}
        />
      </div>

      <HiddenPanel tasks={hiddenTasks} onOpen={openEdit} onShow={handleShow} />

      {snoozeTarget && (
        <SnoozeMenu
          x={snoozeTarget.x}
          y={snoozeTarget.y}
          onPick={(atIso) => applySnooze(snoozeTarget.task, atIso)}
          onClose={() => setSnoozeTarget(null)}
        />
      )}

      {ladder && (
        <TimeLadder
          x={ladder.x}
          y={ladder.y}
          dayKey={ladder.dayKey}
          task={ladder.item.task}
          showCurrentChoice={
            !!ladder.item.task.due_at
            && toLocalDate(new Date(ladder.item.task.due_at)) !== ladder.dayKey
          }
          onPick={commitLadder}
          onClose={() => setLadder(null)}
        />
      )}

      {planOpen && plan && focusDate && (
        <PlanDayDialog
          day={focusDate}
          plan={plan}
          onApply={(slots) => void applyPlan(slots)}
          onClose={() => setPlanOpen(false)}
        />
      )}
    </div>
  );
}

/** Zero-state when the matrix system is off: the queue/strip still work off the
 *  classic flags, but the board needs the matrix — offer to switch it on. */
function EnableMatrixCard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const setMatrixScope = useSettingsStore((s) => s.setMatrixScope);
  async function enableForTasks() {
    try {
      await setMatrixScope("tasks");
    } catch {
      toast.error(t("priorities.update.failed"));
    }
  }
  return (
    <Card className="flex flex-col items-center gap-3 p-8 text-center">
      <Grid3x3 className="h-10 w-10 text-muted-foreground/60" aria-hidden />
      <h2 className="text-lg font-semibold">{t("priorities.enable.title")}</h2>
      <p className="max-w-sm text-sm text-muted-foreground">{t("priorities.enable.body")}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button onClick={() => void enableForTasks()}>{t("priorities.enable.forTasks")}</Button>
        <Button variant="outline" onClick={() => navigate("/settings/tasks")}>{t("priorities.enable.settings")}</Button>
      </div>
    </Card>
  );
}
